<template>
  <base-card :disabled="!available" tabled>
    <template v-slot:header>
      <base-card-heading>
        Existing Funding
      </base-card-heading>
      <div>
        <base-button v-if="available && !project.stages.funding" class="mr-2" secondary small @click="confirm">
          Confirm Funding
        </base-button>
        <base-button modal="#fundingModal" secondary small>
          Add Existing Funding
        </base-button>
      </div>
    </template>
    <template v-if="available">
      <base-stats v-model="stats" />
      <template v-if="project.funding.length">
        <funding-table v-model="funding" @edit="edit" @remove="remove" />
      </template>
      <template v-else>
        <div class="p-4 text-center">
          <p>
            No funding has been added yet.
          </p>
          <base-button :class="{ invisible: !available }" modal="#fundingModal" secondary small>
            Add First Funding
          </base-button>
        </div>
      </template>
      <funding-modal :editing="editing" @closed="editing = null" @created="created" @updated="updated" />
    </template>
    <template v-else>
      <div class="p-4 text-center">
        <p>
          You must submit the financials for this project before you can add funding
        </p>
        <base-button :to="{ name: 'projects.financials', params: { id: project._id } }" primary small>
          Add Financials
        </base-button>
      </div>
    </template>
  </base-card>
</template>

<script>
import { mapState } from 'vuex'
import { EventBus, ProjectCalculations } from '@/libs'

import FundingModal from './FundingModal.vue'
import FundingTable from './FundingTable.vue'

export default {
  created () {
    this.funding = this.$_.cloneDeep(this.project.funding)

    this.stat()
  },

  data: () => ({
    editing: {},
    funding: [],
    stats: []
  }),

  name: 'ProjectFunding',

  components: {
    FundingModal,
    FundingTable
  },

  computed: {
    available () {
      return this.project.stages.details && this.project.stages.financials
    },

    ...mapState({
      project: store => store.core.projects.entity
    })
  },

  methods: {
    async created (funding) {
      await this.$store.dispatch('core/projects/funding/add', funding)

      this.funding.push(funding)

      await this.recalculate()

      this.$flash.success('New funding added.')
    },

    async confirm () {
      this.$dialog.confirm('Confirm existing funding is complete?', async () => {
        const { _id, stages } = this.$_.cloneDeep(this.project)

        stages.funding = true

        await this.$store.dispatch('core/projects/save', { _id, stages })

        this.$flash.success('Project funding confirmed.')
      })
    },

    edit (index) {
      this.editing = this.funding[index]
      this.$modal.show('#fundingModal')
    },

    async recalculate () {
      const project = this.$_.cloneDeep(this.project)

      // Recalculate financials.
      project.funding = this.funding
      project.financials = new ProjectCalculations(project)
        .calculate()
        .getProject()
        .financials

      // Update project financials.
      await this.$store.dispatch('core/projects/save', {
        _id: project._id,
        financials: project.financials
      })

      this.stat()
    },

    async remove (funding) {
      await this.$store.dispatch('core/projects/funding/pull', funding)
      await this.recalculate()
    },

    stat () {
      this.stats = [{
        stat: this.$formatting.toCurrency(this.$project.getFundingSecured()),
        text: 'Funding Secured',
        tooltip: 'Totalled amounts for existing funding'
      }, {
        stat: this.$formatting.toCurrency(this.$project.getTotalCost()),
        text: 'Total Cost',
        tooltip: 'All project costs totalled'
      }, {
        stat: `${this.$project.getPercentageFunded()}%`,
        text: 'Funded',
        tooltip: 'The percentage funded from existing funding'
      }, {
        stat: this.$formatting.toCurrency(this.$project.getFundingRequired()),
        text: 'Funding Required',
        tooltip: 'The remaining that requires funding, total cost - funded'
      }]
    },

    async updated (funding) {
      this.funding.filter((fund, i) => {
        if (fund._id === funding._id) {
          this.funding[i] = funding
        }
      })

      await this.recalculate()

      EventBus.$emit('reloadFundingTable')

      this.$flash.success('Funding updated.')
    }
  }
}
</script>
